/**
 * PaymentPageLayout - Responsive layout for Payment Page V2
 *
 * Desktop: Two columns (left: content, right: sticky payment card)
 * Mobile: Single column with specific ordering per Figma design
 *
 * CRITICAL: Payment card with Stripe Elements must only be rendered ONCE.
 * This layout uses CSS Grid with named areas to position the single
 * payment card instance in different visual positions per breakpoint.
 */

import { ReactNode } from 'react';
import '../dashboard/dashboard-reset.css';
import './PaymentPageLayout.css';

interface PaymentPageLayoutProps {
  /** Hero section (trial badge + headline) */
  hero: ReactNode;
  /** Payment card content */
  paymentCard: ReactNode;
  /** Features list ("What's included") */
  featuresList: ReactNode;
  /** Testimonials section */
  testimonials: ReactNode;
  /** Privacy section */
  privacy: ReactNode;
  /** FAQ section */
  faqs: ReactNode;
  /** Contact button */
  contactButton: ReactNode;
  /** Header content */
  header?: ReactNode;
  /** Footer content */
  footer?: ReactNode;
  /** Sticky mobile CTA */
  stickyMobileCTA?: ReactNode;
}

export function PaymentPageLayout({
  hero,
  paymentCard,
  featuresList,
  testimonials,
  privacy,
  faqs,
  contactButton,
  header,
  footer,
  stickyMobileCTA,
}: PaymentPageLayoutProps) {
  return (
    <div className="dashboard-reset min-h-screen bg-white">
      {/* Container - ~1024px max width for desktop two-column layout */}
      <div className="mx-auto max-w-5xl px-4 pb-24 sm:px-6 md:pb-8 lg:px-8">
        {/* Header - mobile and desktop variants live inside Header component */}
        {header && <div className="relative">{header}</div>}

        {/* Main Layout */}
        <div className="payment-layout pt-2 lg:py-8">
          {/* Left Column - wraps content on desktop for independent flow */}
          <div className="payment-layout__left-column">
            {/* Hero */}
            <div className="payment-layout__hero">{hero}</div>

            {/* Features */}
            <div className="payment-layout__features">
              {featuresList}
            </div>

            {/* Testimonials */}
            <div className="payment-layout__testimonials">{testimonials}</div>

            {/* Privacy */}
            <div className="payment-layout__privacy">{privacy}</div>
          </div>

          {/* Payment Card (sidebar on desktop) */}
          <div className="payment-layout__sidebar">
            <div className="flex flex-col gap-8 lg:sticky lg:top-8">
              {paymentCard}
              {/* FAQs + Contact - shown on desktop sidebar */}
              <div className="hidden lg:block">
                {faqs}
                <div className="mt-8 flex justify-center">{contactButton}</div>
              </div>
            </div>
          </div>

          {/* FAQs + Contact - shown on mobile only, after testimonials */}
          <div className="payment-layout__faqs lg:hidden">
            {faqs}
            <div className="mt-8 pb-8 flex justify-center">{contactButton}</div>
          </div>
        </div>

        {/* Footer */}
        {footer && (
          <div className="border-t border-slate-100 py-8">{footer}</div>
        )}
      </div>

      {/* Sticky Mobile CTA - Fixed at bottom on mobile only */}
      {stickyMobileCTA}
    </div>
  );
}

export default PaymentPageLayout;
